import React, { useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { StepIndicator } from '../components/StepIndicator';
import { UploadIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const STEPS = ['Master Creative', 'Campaign Setup', 'Select Creative', 'Styling Template', 'Variable Content', 'Generate', 'Review & Publish'];
export function VariableContent() {
  const navigate = useNavigate();
  const [pricingMode, setPricingMode] = useState<'same' | 'regional' | 'csv'>('same');
  const [offerMode, setOfferMode] = useState<'same' | 'regional' | 'csv'>('same');
  const [festivalMode, setFestivalMode] = useState<'same' | 'regional' | 'csv'>('same');
  const [pricing, setPricing] = useState('');
  const [offer, setOffer] = useState('');
  const [festival, setFestival] = useState('');
  const canContinue = pricing || offer || festival;
  return <div className="flex w-full min-h-screen bg-gray-50" data-id="element-2323">
      <Sidebar data-id="element-2324" />
      <main className="flex-1" data-id="element-2325">
        <Header data-id="element-2326" />
        <StepIndicator currentStep={5} steps={STEPS} data-id="element-2327" />
        <div className="px-6 py-8" data-id="element-2328">
          <div className="max-w-4xl mx-auto" data-id="element-2329">
            <h1 className="text-2xl font-semibold text-gray-800 mb-2" data-id="element-2330">
              Step 4: Fill Variable Content
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-2331">
              Configure pricing, offers, and festival text for your campaigns
            </p>

            <div className="space-y-6" data-id="element-2332">
              {/* State-wise Pricing */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2333">
                <h3 className="font-medium text-gray-800 mb-4" data-id="element-2334">
                  A. State-wise Pricing
                </h3>
                <div className="flex space-x-3 mb-4" data-id="element-2335">
                  <button onClick={() => setPricingMode('same')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${pricingMode === 'same' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2336">
                    Same for All
                  </button>
                  <button onClick={() => setPricingMode('regional')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${pricingMode === 'regional' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2337">
                    Region-wise
                  </button>
                  <button onClick={() => setPricingMode('csv')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${pricingMode === 'csv' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2338">
                    Upload CSV
                  </button>
                </div>

                {pricingMode === 'same' && <input type="text" value={pricing} onChange={e => setPricing(e.target.value)} placeholder="e.g., Starting at ₹12,50,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2339" />}

                {pricingMode === 'regional' && <div className="space-y-3" data-id="element-2340">
                    <div className="grid grid-cols-2 gap-3" data-id="element-2341">
                      <div data-id="element-2342">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-2343">
                          North Zone
                        </label>
                        <input type="text" placeholder="₹12,50,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2344" />
                      </div>
                      <div data-id="element-2345">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-2346">
                          South Zone
                        </label>
                        <input type="text" placeholder="₹12,75,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2347" />
                      </div>
                      <div data-id="element-2348">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-2349">
                          East Zone
                        </label>
                        <input type="text" placeholder="₹12,60,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2350" />
                      </div>
                      <div data-id="element-2351">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-2352">
                          West Zone
                        </label>
                        <input type="text" placeholder="₹12,80,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2353" />
                      </div>
                    </div>
                  </div>}

                {pricingMode === 'csv' && <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center" data-id="element-2354">
                    <UploadIcon className="mx-auto text-gray-400 mb-3" size={32} data-id="element-2355" />
                    <p className="text-sm text-gray-600 mb-3" data-id="element-2356">
                      Upload CSV with dealer-specific pricing
                    </p>
                    <label className="inline-block bg-blue-500 text-white px-4 py-2 rounded-md text-sm cursor-pointer hover:bg-blue-600" data-id="element-2357">
                      Choose CSV File
                      <input type="file" accept=".csv" className="hidden" data-id="element-2358" />
                    </label>
                    <p className="text-xs text-gray-500 mt-2" data-id="element-2359">
                      Format: dealer_id, pricing
                    </p>
                  </div>}
              </div>

              {/* Offer/Discount Text */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2360">
                <h3 className="font-medium text-gray-800 mb-4" data-id="element-2361">
                  B. Offer/Discount Text
                </h3>
                <div className="flex space-x-3 mb-4" data-id="element-2362">
                  <button onClick={() => setOfferMode('same')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${offerMode === 'same' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2363">
                    Same for All
                  </button>
                  <button onClick={() => setOfferMode('regional')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${offerMode === 'regional' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2364">
                    Region-wise
                  </button>
                  <button onClick={() => setOfferMode('csv')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${offerMode === 'csv' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2365">
                    Dealer-specific CSV
                  </button>
                </div>

                {offerMode === 'same' && <textarea value={offer} onChange={e => setOffer(e.target.value)} placeholder="e.g., Get ₹50,000 off + Free accessories worth ₹25,000" rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2366" />}

                {offerMode === 'regional' && <div className="space-y-3" data-id="element-2367">
                    {['North', 'South', 'East', 'West'].map(zone => <div key={zone} data-id="element-2368">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-2369">
                          {zone} Zone Offer
                        </label>
                        <input type="text" placeholder={`Special offer for ${zone} zone`} className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2370" />
                      </div>)}
                  </div>}

                {offerMode === 'csv' && <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center" data-id="element-2371">
                    <UploadIcon className="mx-auto text-gray-400 mb-3" size={32} data-id="element-2372" />
                    <p className="text-sm text-gray-600 mb-3" data-id="element-2373">
                      Upload CSV with dealer-specific offers
                    </p>
                    <label className="inline-block bg-blue-500 text-white px-4 py-2 rounded-md text-sm cursor-pointer hover:bg-blue-600" data-id="element-2374">
                      Choose CSV File
                      <input type="file" accept=".csv" className="hidden" data-id="element-2375" />
                    </label>
                  </div>}
              </div>

              {/* Festival Text */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2376">
                <h3 className="font-medium text-gray-800 mb-4" data-id="element-2377">
                  C. Festival Text
                </h3>
                <div className="flex space-x-3 mb-4" data-id="element-2378">
                  <button onClick={() => setFestivalMode('same')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${festivalMode === 'same' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2379">
                    Same for All
                  </button>
                  <button onClick={() => setFestivalMode('regional')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${festivalMode === 'regional' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2380">
                    Regional Mapping
                  </button>
                  <button onClick={() => setFestivalMode('csv')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${festivalMode === 'csv' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2381">
                    Dealer-specific CSV
                  </button>
                </div>

                {festivalMode === 'same' && <input type="text" value={festival} onChange={e => setFestival(e.target.value)} placeholder="e.g., Diwali Special Offer" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2382" />}

                {festivalMode === 'regional' && <div className="space-y-3" data-id="element-2383">
                    <div data-id="element-2384">
                      <label className="block text-sm text-gray-600 mb-1" data-id="element-2385">
                        North India
                      </label>
                      <input type="text" placeholder="e.g., Diwali Dhamaka" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2386" />
                    </div>
                    <div data-id="element-2387">
                      <label className="block text-sm text-gray-600 mb-1" data-id="element-2388">
                        South India
                      </label>
                      <input type="text" placeholder="e.g., Pongal Special" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2389" />
                    </div>
                    <div data-id="element-2390">
                      <label className="block text-sm text-gray-600 mb-1" data-id="element-2391">
                        East India
                      </label>
                      <input type="text" placeholder="e.g., Durga Puja Offer" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2392" />
                    </div>
                    <div data-id="element-2393">
                      <label className="block text-sm text-gray-600 mb-1" data-id="element-2394">
                        West India
                      </label>
                      <input type="text" placeholder="e.g., Ganesh Chaturthi Special" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2395" />
                    </div>
                  </div>}

                {festivalMode === 'csv' && <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center" data-id="element-2396">
                    <UploadIcon className="mx-auto text-gray-400 mb-3" size={32} data-id="element-2397" />
                    <p className="text-sm text-gray-600 mb-3" data-id="element-2398">
                      Upload CSV with dealer-specific festival text
                    </p>
                    <label className="inline-block bg-blue-500 text-white px-4 py-2 rounded-md text-sm cursor-pointer hover:bg-blue-600" data-id="element-2399">
                      Choose CSV File
                      <input type="file" accept=".csv" className="hidden" data-id="element-2400" />
                    </label>
                  </div>}
              </div>
            </div>

            <div className="flex justify-between mt-6" data-id="element-2401">
              <button onClick={() => navigate('/dealerization/step-3')} className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-2402">
                Back
              </button>
              <button onClick={() => navigate('/dealerization/step-5')} disabled={!canContinue} className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-2403">
                Continue to Generate Creatives
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>;
}